<template>
    <div ref="outerElementRef" class="settings-favorite-channels-outer">
        <favorite-channels-search
            ref="search"
            v-model="searchValue"
            :not-found="isEmptyChannels"
            @return-focus="focusRoot"
            @back-to-navigation="goToBack"
        />
        <div
            ref="root"
            class="settings-favorite-channels"
            tabindex="0"
            :style="rootStyles"
            @focusin="setSelfFocused"
            @focusout="unsetSelfFocused"
        >
            <div
                v-if="isEnabledArrowUp"
                ref="arrowUp"
                class="settings-favorite-channels__arrow settings-favorite-channels__arrow_up"
                @click="clickUp"
            > 
                <common-icon :icon-name="'IconArrow'" />
            </div>
            <div
                v-show="!isEmptyChannels"
                ref="viewport"
                class="settings-favorite-channels__viewport"
                :style="viewportStyles"
            >
                <favorite-channel-item
                    v-for="(item, index) of viewedChannels"
                    ref="items"
                    :key="item.id || index"
                    :item="item"
                    :is-checked="isItemFavorite(item)"
                    :is-focused="isItemFocused(item)"
                    class="settings-favorite-channels__item"
                    @click="toggleFavorite(item)"
                />
            </div>
            <div
                v-if="isEnabledArrowDown"
                ref="arrowDown"
                class="settings-favorite-channels__arrow settings-favorite-channels__arrow_down"
                @click="clickDown"
            > 
                <common-icon :icon-name="'IconArrow'" />
            </div>
            <slot />
        </div>
    </div>
</template>

<script setup lang="ts">
    import { type Ref, ref, computed, watch, onBeforeMount, onBeforeUnmount, nextTick, type StyleValue } from "vue";
    import type { Channel } from "@ca.vmedia.tv/domains";
    import { useChannelsStore } from "@/stores/Channels";
    import { ContentType, ProtoItem } from "@ca.vmedia.tv/domains";
    import { useFocusedComponents } from '@/composables/useFocusedComponents';
    import { useFavorite } from "@/composables/useFavorite";
    import { useFavoriteChannelsStore } from "@/stores/FavoriteChannels";
    import { storeToRefs } from 'pinia';
    import { 
        onBeforeRouteLeave, 
        type NavigationGuardNext, 
        type RouteLocationNormalizedGeneric, 
        type RouteLocationNormalizedLoadedGeneric, 
    } from 'vue-router';
    import { useLinesMyTvStore } from "@/stores/LinesMyTv";
    import type { FavoriteChannelsSearchRef } from "@/components/widgets/SettingsFavoriteChannels/components/FavoriteChannelsSearch/types";

    const ChannelsStore = useChannelsStore();
    const {
        getFilteredChannels,
    } = ChannelsStore;
    const {
        getAvailableChannels,
    } = storeToRefs(ChannelsStore);

     const { clearAll: clearAllMyTv } = useLinesMyTvStore();

    const FavoriteChannelsStore = useFavoriteChannelsStore();
    const {
        fetchFavChannelsIds,
    } = FavoriteChannelsStore;
    const {
        getFavChannelsIds: favChannels,
    } = storeToRefs(FavoriteChannelsStore);


    const { deleteFromApiFavorites, addToApiFavorites, deleteSavedIdsFavorites } = useFavorite();

    const emit = defineEmits(['back-to-navigation', 'data-loading']);
    const { setFocused, unsetFocused, outerElementRef } = useFocusedComponents('PreferencesPage');
    const { $navigator } = useNuxtApp();
    const viewportItemsCount: Ref<number> = ref(0);
    const topViewedItemIndex: Ref<number> = ref(0);
    const bottomViewedItemIndex: Ref<number> = ref(0);
    const selectedItemIndex: Ref<number> = ref(0);
    const arrowUp: Ref<HTMLElement | null> = ref(null);
    const arrowDown: Ref<HTMLElement | null> = ref(null); 
    const root: Ref<HTMLDivElement | null> = ref(null); 
    const addedChannels: Ref<number[]> = ref([]);
    const removedChannels: Ref<number[]> = ref([]);
    const isFocused: Ref<boolean> = ref(false);
    const isRegKeyListeners: Ref<boolean> = ref(false);
    const search: Ref<FavoriteChannelsSearchRef | null> = ref(null);
    const searchValue: Ref<string> = ref('');
    const allChannels: Ref<Channel[]> = ref([]);
    const rootStyles: Ref<StyleValue> = ref({});

    const isEmptyChannels = computed((): boolean => allChannels.value.length === 0);
    const isEnabledArrowUp = computed((): boolean =>  topViewedItemIndex.value > 0);
    const isEnabledArrowDown = computed((): boolean =>  allChannels.value.length > bottomViewedItemIndex.value + 1);
    const viewedChannels = computed((): Channel[] => {
        return allChannels.value
            .filter((_, idx: number) => (
                idx >= topViewedItemIndex.value && idx <= bottomViewedItemIndex.value
            ));
    });
    const itemHeight = computed((): number => {
        const style = window.getComputedStyle(document.body);
        const height = style.getPropertyValue("--favorite-channel-item-height");
        return Number.parseInt(height.replace(/\D/g, "")) || 0;
    });
    const viewportStyles = computed((): StyleValue => {
        const propValue = `calc(((100% - var(--favorite-channel-item-height) * ${viewportItemsCount.value}) / 2))`;
        return {
            top: propValue,
            bottom: propValue,
        };
    });
    const isItemFavorite = (item: Channel): boolean => {
        if (!item.id) {
            return false;
        }
        return addedChannels.value.includes(item.id);
    };

    const goToBack = (): void => {
        emit("back-to-navigation");
    }
    const focusToSearch = (): void => {
        search.value?.focusToInput();
    }
    const setRootStyles = (): void => {
        rootStyles.value = {
            height: `calc(${viewportItemsCount.value} * ${itemHeight.value}px)`,
        };
    }
    const setViewportItemsCount = (): void => {
        const rootHeight = root.value?.clientHeight ?? 1;
        viewportItemsCount.value = Math.floor(rootHeight / itemHeight.value);
        setRootStyles();
    };
    const addClassArrowFocused = (element: Element | null) => {
        if (element === null) {
            return;
        }
        element.classList.add('settings-favorite-channels__arrow_focused');
    };
    const clickUp = (event: MouseEvent) => {
        addClassArrowFocused(arrowUp.value);
        setTimeout(() => {
            if (arrowUp.value === null) {
                return;
            }
            onKeyUp(event);
            addClassArrowFocused(arrowUp.value);
        }, 100);
    };
    const clickDown = (event: MouseEvent) => {
        addClassArrowFocused(arrowDown.value);
        setTimeout(() => {
            onKeyDown(event);
            addClassArrowFocused(arrowDown.value);
        }, 100);
    };
    const onKeyUp = (event: KeyboardEvent | MouseEvent | HTMLElement | null): void => {
        if (event instanceof KeyboardEvent && event.key === "ArrowUp") {
            event.stopPropagation();
        }
        if (selectedItemIndex.value === 0) {
            search.value?.focusToInput();
            return;
        }
        selectedItemIndex.value -= 1;
    };
    const onKeyDown = (event: KeyboardEvent | MouseEvent | HTMLElement | null): void => {
        if (event instanceof KeyboardEvent && event.key === "ArrowDown") {
            event.stopPropagation();
        }
        if (selectedItemIndex.value === allChannels.value.length - 1) {
            return;
        }
        selectedItemIndex.value += 1;
    };
    const onKeyEnter = (): void => {
        toggleFavorite(null);
    };

    const setHandlers = (): void => {
        $navigator.on("keypress:up", onKeyUp);
        $navigator.on("keypress:down", onKeyDown);
        $navigator.on("keypress:enter", onKeyEnter);
    };
    const unsetHandlers = (): void => {
        $navigator.off("keypress:up", onKeyUp);
        $navigator.off("keypress:down", onKeyDown);
        $navigator.off("keypress:enter", onKeyEnter);
    };
    const isItemFocused = (item: Channel): boolean => {
        const allChannelsIdx = allChannels.value.indexOf(item);
        return isFocused.value && allChannelsIdx === selectedItemIndex.value;
    };
    const toggleFavorite = (item: Channel | null | undefined): void => {
        const useSelected: boolean = !item;
        let itemId: number | null;

        if (useSelected) {
            const itemFromAll = allChannels.value[selectedItemIndex.value];
            itemId = itemFromAll?.id;
            if (!itemId) {
                return;
            }
        } else {
            itemId = item?.id as number;
            selectedItemIndex.value = allChannels.value.indexOf(item as Channel);
        }

        let added: number[], removed: number[] = [];

        if (addedChannels.value.includes(itemId)) {
            if (addedChannels.value.includes(itemId)) {
                removed = [ ...removedChannels.value, itemId ];
            }
            added = addedChannels.value.filter(i => i !== itemId);
        } else {
            removed = removedChannels.value.filter(i => i !== itemId);
            added = [ ...addedChannels.value, itemId ];
        }

        addedChannels.value = [...added];
        removedChannels.value = [...removed];
    };

    const blurRoot = () => {
        if (!root.value) {
            return;
        }
        root.value.blur();
    };
    const focusRoot = () => {
        if (!root.value) {
            return;
        }
        root.value.focus();
    };

    const registerKeyListeners = (): void => {
        if (!isRegKeyListeners.value) {
            $navigator.on("keypress:left", goToBack);
            $navigator.on("keypress:back", focusToSearch);
            isRegKeyListeners.value = true;
        }
        
    };
    const unregisterKeyListeners = (): void => {
        if (isRegKeyListeners.value) {
            $navigator.off("keypress:left", goToBack);
            $navigator.off("keypress:back", focusToSearch);
            isRegKeyListeners.value = false;
        }
    };

    const sortingChannels = (_targetChannels: Channel[]): Channel[] => {
        return [..._targetChannels].sort((a, b) => {
            const aIsAdded = addedChannels.value.includes(a.id ?? 0);
            const bIsAdded = addedChannels.value.includes(b.id ?? 0);
            if (aIsAdded === bIsAdded) {
                const aChannel = _targetChannels.find(channel => channel.id === a.id);
                const bChannel = _targetChannels.find(channel => channel.id === b.id);
                return (aChannel?.accessNum ?? 0) - (bChannel?.accessNum ?? 0);
            }
            return aIsAdded ? -1 : 1;
        });
    };

    const initChannels = (callback?: () => void): void => {
        allChannels.value = searchValue.value.length
            ? sortingChannels(getFilteredChannels(searchValue.value))
            : sortingChannels(getAvailableChannels.value);
        if (callback) {
           void nextTick(callback);
        }
    };

    const clearSelectedItemIndex = (): void => {
        // Scrolling up the list if the search input is focused by a magic remote
        selectedItemIndex.value = 0;
        topViewedItemIndex.value = 0;
    };


    watch (viewportItemsCount, (value: number) => {
        bottomViewedItemIndex.value = topViewedItemIndex.value + value - 1;
    });
    watch (topViewedItemIndex, (value: number) => {
        bottomViewedItemIndex.value = value + viewportItemsCount.value - 1;
    });
    watch (selectedItemIndex, (value: number) => {
        if (value > bottomViewedItemIndex.value) {
            topViewedItemIndex.value += 1;
            return;
        }
        if (value < topViewedItemIndex.value) {
            topViewedItemIndex.value -= 1;
            return;
        }
    });
    watch (isFocused, (value: boolean) => {
        if (value) {
            setHandlers();
            focusRoot();
        } else {
            unsetHandlers();
            blurRoot();
        }
    });

    const initialLoadData = async (): Promise<void> => {
        emit("data-loading", true);
        await deleteSavedIdsFavorites();
        await loadFavorites()
        .finally(() => {
            emit("data-loading", false);
        });
    };
    const  loadFavorites = async (): Promise<void> => {
        await fetchFavChannelsIds();
        addedChannels.value = [ ...favChannels.value, ...addedChannels.value ];
    };

    const setSelfFocused = (): void => {
        if (!isFocused.value) {
            registerKeyListeners();
            isFocused.value = true;
            setFocused();
        }
    };
    const unsetSelfFocused = (): void => {
        if (isFocused.value) {
            unregisterKeyListeners();
            isFocused.value = false;
            unsetFocused();
        }
    };
    const  updateFavoritesApi = async(): Promise<void> => {
        const newlyRemoved = removedChannels.value.filter(i => favChannels.value.includes(i));

        if (newlyRemoved.length) {
            await deleteFromApiFavorites(removedChannels.value.map(id => new ProtoItem(id, ContentType.LiveTV as number)), true);
        }
        const newlyAdded = addedChannels.value.filter(i => !favChannels.value.includes(i));

        if (newlyAdded.length) {
            await addToApiFavorites(newlyAdded.map(id => new ProtoItem(id, ContentType.LiveTV as number)), true);
        }
    };

    onBeforeRouteLeave((_to: RouteLocationNormalizedGeneric, _from: RouteLocationNormalizedLoadedGeneric, next: NavigationGuardNext): void => {
        updateFavoritesApi().then(() => {
            clearAllMyTv();
            
            next();
        }).catch((error) => {
            Logger.captureError(error as Error);
            clearAllMyTv();
            next();
        });
    });
    onBeforeUnmount(() => {
        unsetSelfFocused();
    });
    onBeforeMount(async () => {
        await initialLoadData();
        initChannels(setViewportItemsCount);
        search.value?.focusToInput();
    });

    watch(searchValue, () => {
        initChannels();
        clearSelectedItemIndex();
    });
</script>

<style lang="scss">
    @import url("styles.scss");
</style>